import { yupResolver } from '@hookform/resolvers/yup'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { Validation } from '@percent/admin-dashboard/api/types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ActionModal, Alert, Flex, FormField, Modal, Radio, TextInput, Typography } from '@percent/lemonade'
import { useMutation } from '@percent/utility'

type UpdateEmailModalProps = {
  isModalOpened: boolean
  setModalOpened: Dispatch<SetStateAction<boolean>>
  refresh: () => void
  validation: Validation | undefined
  foundEmails: { email: string; pageUrl: string }[] | null
}

const emailOrEmptySchema = yup.string().email()

export function UpdateEmailModal({
  isModalOpened,
  setModalOpened,
  refresh,
  validation,
  foundEmails
}: UpdateEmailModalProps) {
  const { t } = useTranslation()
  const [showManualSubmit, setShowManualSubmit] = useState(false)
  const { adminService } = useServices()

  const hasExistingEmail = !!validation?.email
  const hasFoundEmails = !!foundEmails?.length

  const [{ isPending }, { apiFunc: approveFunc }] = useMutation(adminService.editValidationRequest, () => {
    refresh()
    setModalOpened(false)
  })

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .test('email-or-empty', t('errorMessage.invalidEmail'), value =>
            !value ? true : emailOrEmptySchema.isValidSync(value)
          )
      }),
    [t]
  )

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isValid, isDirty }
  } = useForm<{ email: string }>({
    defaultValues: { email: validation?.email ?? '' },
    mode: 'onChange',
    resolver: yupResolver(validationSchema)
  })

  const emailValue = watch('email')
  const isRemovingEmail = hasExistingEmail && isDirty && emailValue.length === 0

  useEffect(() => {
    if (isModalOpened) {
      reset({ email: validation?.email ?? '' })
      setShowManualSubmit(false)
    }
  }, [isModalOpened, reset, validation?.email])

  const handleModalClose = () => setModalOpened(false)

  const submitButtonText = isRemovingEmail
    ? t('dialog.updateEmail.removeSubmit')
    : hasExistingEmail
    ? t('button.editEmail')
    : t('dialog.validationUpdateEmail.submit')

  return (
    <Modal open={isModalOpened} onClose={handleModalClose}>
      <ActionModal
        title={t('dialog.validationUpdateEmail.title')}
        primaryButtonText={submitButtonText}
        secondaryButtonText={t('button.cancel')}
        disabled={!isValid || !isDirty || isPending || (!hasExistingEmail && !emailValue.length)}
        handleClose={handleModalClose}
        handleSubmit={handleSubmit(({ email }) => {
          if (!validation) return
          approveFunc({ id: validation.id, payload: { email: email.length > 0 ? email : null } })
        })}
      >
        <Flex direction="column" gap={16}>
          {hasFoundEmails && <Typography>{t('dialog.validationUpdateEmail.description')}</Typography>}
          <Flex direction="column" gap={8}>
            {hasFoundEmails && (
              <>
                <Typography>{t('dialog.validationUpdateEmail.suggestedEmails')}</Typography>
                <Flex direction="column" gap={4}>
                  {foundEmails.map(({ email, pageUrl }) => (
                    <Radio
                      key={email}
                      id={email}
                      name="email"
                      label={
                        <Typography variant="default-medium">
                          {email}{' '}
                          <a href={pageUrl} target="_blank" rel="noopener noreferrer">
                            ({t('typography.link')})
                          </a>
                        </Typography>
                      }
                      value={email}
                      checked={email === emailValue}
                      onChange={() => {
                        setValue('email', email, { shouldValidate: true, shouldDirty: true })
                        setShowManualSubmit(false)
                      }}
                    />
                  ))}
                  <Radio
                    id="manual-submit"
                    label={
                      <Typography>
                        <i>{t('dialog.validationUpdateEmail.manualSubmit.radio')}</i>
                      </Typography>
                    }
                    name="email"
                    checked={showManualSubmit}
                    onChange={() => {
                      setShowManualSubmit(v => !v)
                      setValue('email', '', { shouldValidate: false, shouldDirty: hasExistingEmail })
                    }}
                  />
                </Flex>
              </>
            )}
            {(showManualSubmit || !hasFoundEmails) && (
              <>
                <FormField
                  label={t('dialog.validationUpdateEmail.manualSubmit.label')}
                  statusMessage={errors.email?.message}
                  status={errors.email ? 'danger' : 'default'}
                  necessity="optional"
                >
                  <TextInput
                    name="email"
                    id="manual-submit-input"
                    placeholder={t('dialog.validationUpdateEmail.manualSubmit.placeholder')}
                    onChange={e => setValue('email', e.target.value, { shouldValidate: true, shouldDirty: true })}
                    value={emailValue}
                  />
                </FormField>
                <Alert variant="warning" title={t('dialog.updateEmail.removeHint')} />
              </>
            )}
          </Flex>
        </Flex>
      </ActionModal>
    </Modal>
  )
}
